/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  type AutoUpdateOptions,
  FloatingFocusManager,
  FloatingPortal,
  type Placement,
  type UseFloatingOptions,
  useMergeRefs,
} from '@floating-ui/react';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { type CommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { useIsInsideDialog } from '../dialog/dialog-context';
import { useNeedleTheme } from '../theme';
import { Typography, type TypographyVariants } from '../typography';
import { TooltipContext, useTooltip, useTooltipContext } from './use-tooltip';

export type TooltipProps = {
  /** Content displayed inside the tooltip. Wraps `Tooltip.Trigger`, `Tooltip.Content`, and optional parts */
  children: React.ReactNode;
  /** Whether the tooltip is disabled */
  isDisabled?: boolean;
  /** Whether the tooltip should be open on first render  */
  isInitialOpen?: boolean;
  /** Placement of the tooltip content relative to the trigger */
  placement?: Placement;
  /** Controls the open state . If omitted, the tooltip manages its own state */
  isOpen?: boolean;
  // TODO v5: deprecate this prop
  /**
   *  Visual style of the tooltip content. `simple` renders plain text, `rich` renders a composable panel
   *  @deprecated rich type will be removed in v5, use simple type instead.
   */
  type: 'simple' | 'rich';
  /** Whether the content is rendered in a portal.  */
  isPortaled?: boolean;
  /** Callback fired when the open state changes, if omitted, the tooltip manages its own state using internal hooks */
  onOpenChange?: UseFloatingOptions['onOpenChange'];
  /** Positioning strategy of the tooltip. Defaults to `fixed` when inside a dialog, otherwise `absolute` */
  floatingStrategy?: 'absolute' | 'fixed';
  /** Hover open/close delays in milliseconds */
  hoverDelay?: {
    open: number;
    close: number;
  };
  /** Whether the tooltip should close when the reference element is clicked. Useful when the trigger moves after click. */
  shouldCloseOnReferenceClick?: boolean;
  autoUpdateOptions?: AutoUpdateOptions;
  /** Whether the tooltip should follow the mouse cursor. Only applies to 'simple' tooltips with mouse hover. */
  followCursor?: boolean;
};

const TooltipComponent = ({
  children,
  isDisabled = false,
  type,
  isInitialOpen,
  placement,
  isOpen,
  onOpenChange,
  isPortaled: isPortaledProp,
  floatingStrategy: strategyProp,
  hoverDelay,
  shouldCloseOnReferenceClick,
  autoUpdateOptions,
  followCursor,
}: TooltipProps) => {
  const isInsideDialog = useIsInsideDialog();
  const strategy = isInsideDialog ? 'fixed' : 'absolute';
  const isPortaled = !isInsideDialog;

  const tooltip = useTooltip({
    autoUpdateOptions,
    followCursor,
    hoverDelay,
    isDisabled,
    isInitialOpen,
    // if isDisabled is true tooltip will not open otherwise either controlled or uncontrolled depending on if isOpen is passed
    isOpen: isDisabled === true ? false : isOpen,
    isPortaled: isPortaledProp ?? isPortaled,
    onOpenChange,
    placement,
    shouldCloseOnReferenceClick,
    strategy: strategyProp ?? strategy,
    type,
  });

  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
};

TooltipComponent.displayName = 'Tooltip';

type TooltipTriggerProps = {
  /** Content displayed inside the trigger element */
  children: React.ReactNode;
  /** Whether the trigger element can be any element without an extra button wrapper. The child element will be cloned with the necessary trigger props and merged refs */
  hasButtonWrapper?: boolean;
};

const TooltipTrigger = ({
  children,
  hasButtonWrapper = false,
  htmlAttributes,
  className,
  style,
  ref,
  ...restProps
}: CommonProps<'button', TooltipTriggerProps>) => {
  const context = useTooltipContext();

  // Example from floating-ui
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const childrenProps = (children as any).props;
  const mergedRefs = useMergeRefs([
    context.refs.setReference,
    ref,
    childrenProps?.ref,
  ]);

  const triggerClasses = classNames(
    {
      'ndl-closed': !context.isOpen,
      'ndl-open': context.isOpen,
    },
    'ndl-tooltip-trigger',
    className,
  );

  // `hasButtonWrapper=true` allows the user to pass any element as the anchor
  if (hasButtonWrapper && React.isValidElement(children)) {
    const props: Record<string, unknown> = {
      className: triggerClasses,
      ...htmlAttributes,
      ...childrenProps,
      ref: mergedRefs,
    };

    return React.cloneElement(children, context.getReferenceProps(props));
  }

  return (
    <button
      type="button"
      className={triggerClasses}
      style={style}
      ref={mergedRefs}
      {...context.getReferenceProps(htmlAttributes)}
      {...restProps}
    >
      {children}
    </button>
  );
};

type TooltipContentProps = {
  /** Content displayed inside the tooltip */
  children: React.ReactNode;
};

const TooltipContent = ({
  children,
  style,
  htmlAttributes,
  className,
  ref,
  ...restProps
}: CommonProps<'div', TooltipContentProps>) => {
  const context = useTooltipContext();
  const mergedRef = useMergeRefs([context.refs.setFloating, ref]);
  const { themeClassName } = useNeedleTheme();

  const hasHeaderChild = React.useMemo(() => {
    if (context.type !== 'rich') {
      return false;
    }
    const childrenArray = React.Children.toArray(children);
    return childrenArray.some(
      (child) => React.isValidElement(child) && child.type === TooltipHeader,
    );
  }, [children, context.type]);

  if (!context.isOpen) {
    return null;
  }

  const classes = classNames('ndl-tooltip-content', themeClassName, className, {
    'ndl-tooltip-content-rich': context.type === 'rich',
    'ndl-tooltip-content-simple': context.type === 'simple',
  });

  if (context.type === 'simple') {
    return (
      <ConditionalWrap
        shouldWrap={context.isPortaled}
        wrap={(wrapChildren) => <FloatingPortal>{wrapChildren}</FloatingPortal>}
      >
        <div
          ref={mergedRef}
          className={classes}
          style={{
            ...context.floatingStyles,
            ...style,
          }}
          {...restProps}
          {...context.getFloatingProps(htmlAttributes)}
        >
          <Typography variant="body-medium">{children}</Typography>
        </div>
      </ConditionalWrap>
    );
  }

  const ariaLabelledBy =
    htmlAttributes?.['aria-labelledby'] ??
    (hasHeaderChild ? context.headerId : undefined);

  if (htmlAttributes?.['aria-label'] === undefined && !ariaLabelledBy) {
    console.warn(
      'The rich Tooltip is missing aria-label and Header. Please add one of them for accessibility.',
    );
  }

  return (
    <ConditionalWrap
      shouldWrap={context.isPortaled}
      wrap={(wrapChildren) => <FloatingPortal>{wrapChildren}</FloatingPortal>}
    >
      <FloatingFocusManager
        context={context.context}
        returnFocus={true}
        modal={false}
        initialFocus={0}
        closeOnFocusOut
      >
        <div
          ref={mergedRef}
          className={classes}
          style={{
            ...context.floatingStyles,
            ...style,
          }}
          {...restProps}
          {...context.getFloatingProps({
            ...htmlAttributes,
            'aria-labelledby': ariaLabelledBy,
          })}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </ConditionalWrap>
  );
};

type TooltipHeaderProps = {
  /** Content displayed inside the header */
  children: React.ReactNode;
  /** @deprecated Use `typographyVariant` instead */
  passThroughProps?: Partial<React.ComponentPropsWithoutRef<typeof Typography>>;
  typographyVariant?: TypographyVariants;
};

const TooltipHeader = ({
  children,
  passThroughProps,
  typographyVariant = 'subheading-medium',
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'span', TooltipHeaderProps>) => {
  const context = useTooltipContext();

  const classes = classNames('ndl-tooltip-header', className);

  useEffect(() => {
    if (Boolean(htmlAttributes?.id) === true) {
      context.setHeaderId?.(htmlAttributes?.id);
    }
  }, [context, htmlAttributes?.id]);

  if (!context.isOpen) {
    return null;
  }

  return (
    <Typography
      ref={ref}
      variant={typographyVariant}
      className={classes}
      style={style}
      htmlAttributes={{ ...htmlAttributes, id: context.headerId }}
      {...passThroughProps}
      {...restProps}
    >
      {children}
    </Typography>
  );
};

export type TooltipBodyProps = {
  /** Content displayed inside the body */
  children: React.ReactNode;
  /** Additional props to pass to the Typography component */
  // TODO v5: Deprecate passThroughProps and use restProps instead
  passThroughProps?: Partial<React.ComponentPropsWithoutRef<typeof Typography>>;
};

const TooltipBody = ({
  children,
  className,
  style,
  htmlAttributes,
  passThroughProps,
  ref,
  ...restProps
}: CommonProps<'span', TooltipBodyProps>) => {
  const context = useTooltipContext();

  const classes = classNames('ndl-tooltip-body', className);
  if (!context.isOpen) {
    return null;
  }

  return (
    <Typography
      ref={ref}
      variant="body-medium"
      className={classes}
      style={style}
      htmlAttributes={htmlAttributes}
      {...passThroughProps}
      {...restProps}
    >
      {children}
    </Typography>
  );
};

type ToolTipActionsProps = {
  /** Content displayed inside the actions container */
  children: React.ReactNode;
};

const Actions = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', ToolTipActionsProps>) => {
  const context = useTooltipContext();
  const mergedRefs = useMergeRefs([context.refs.setFloating, ref]);

  if (!context.isOpen) {
    return null;
  }

  const classes = classNames('ndl-tooltip-actions', className);

  return (
    <div
      className={classes}
      ref={mergedRefs}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

Actions.displayName = 'Tooltip.Actions';

const Tooltip = Object.assign(TooltipComponent, {
  Actions: Actions,
  Body: TooltipBody,
  Content: TooltipContent,
  Header: TooltipHeader,
  Trigger: TooltipTrigger,
});

export { Tooltip };
